import { Barcode, StoreProduct } from '@bofrak-backend/shared';
import Dexie, { Table } from 'dexie';
import { getLatestUpdatedAt, shouldReplaceExisting } from './helpers';
import { SyncResponseStatus } from './types';

export interface DBStoreProduct extends StoreProduct {
  _lastSynced?: Date;
  _localChanges?: boolean;
}

export interface BarcodeIndexEntry {
  store_id: string;
  barcode_value: string;
  product_id: string;
  product_name: string;
}

export class ProductDatabase extends Dexie {
  storeProducts!: Table<DBStoreProduct, [string, string]>;
  syncMetadata!: Table<{
    id: string;
    lastSync: Date;
    storeId: string;
    totalProducts: number;
    syncStatus: SyncResponseStatus;
    errorMessage?: string;
  }>;
  productBarcodes!: Table<BarcodeIndexEntry>;

  constructor() {
    super('ShopAndSmileProductDB');

    this.version(3).stores({
      storeProducts: `
        &[store_id+id],
        merchant_id,
        is_parent,
        store_sku,
        store_id,
        is_available_for_sale,
        is_manually_controlled,
        is_sold_online,
        is_available_in_store,
        [merchant_id+store_id],
        [store_id+is_parent],
        [store_id+store_sku],
        [store_id+is_available_for_sale],
        [store_id+is_manually_controlled],
        [store_id+is_sold_online],
        [store_id+is_available_in_store]
      `,
      syncMetadata: 'id, storeId',
    });

    this.version(4).stores({
      productBarcodes: `&[store_id+barcode_value+product_id], [store_id+barcode_value], [store_id+product_id], store_id`,
    });
  }

  async getStoreProducts(
    storeId: string,
    includeDeleted = false,
  ): Promise<StoreProduct[]> {
    const query = this.storeProducts.where('store_id').equals(storeId);

    if (!includeDeleted) {
      const products = await query.toArray();
      return products.filter((p) => !p.deleted_at || p.deleted_at === '');
    }

    return query.toArray();
  }

  async bulkUpdateProducts(
    products: StoreProduct[],
    storeId: string,
  ): Promise<void> {
    const existingProducts = await this.storeProducts
      .where('store_id')
      .equals(storeId)
      .toArray();

    const existingMap = new Map(
      existingProducts.map((p) => [`${p.store_id}-${p.id}`, p]),
    );

    const toAdd: StoreProduct[] = [];
    const toUpdate: StoreProduct[] = [];
    const toDelete: string[] = [];

    for (const product of products) {
      const key = `${product.store_id}-${product.id}`;
      const existing = existingMap.get(key);

      const latestUpdatedAt =
        getLatestUpdatedAt(product) ||
        product.updated_at ||
        new Date().toISOString();

      const dbProduct: DBStoreProduct = {
        ...product,
        _lastSynced: new Date(),
        updated_at: latestUpdatedAt,
      };

      if (!existing) {
        toAdd.push(dbProduct);
      } else {
        // Always update existing products with remote data
        // Remote is the source of truth
        toUpdate.push(dbProduct);
      }

      existingMap.delete(key);
    }

    for (const [key] of existingMap) {
      toDelete.push(key);
    }

    await this.transaction('rw', this.storeProducts, async () => {
      if (toAdd.length > 0) {
        await this.storeProducts.bulkPut(toAdd);
      }
      if (toUpdate.length > 0) {
        await this.storeProducts.bulkPut(toUpdate);
      }
      if (toDelete.length > 0) {
        for (const key of toDelete) {
          const [storeIdValue, productId] = key.split('-');
          await this.storeProducts.delete([storeIdValue, productId]);
        }
      }
    });

    // Rebuild barcode index after bulk sync
    await this.rebuildBarcodeIndex(storeId);
  }

  async updateSingleProduct(
    product: StoreProduct,
    forceUpdate = false,
  ): Promise<void> {
    const existing = await this.storeProducts.get([
      product.store_id,
      product.id,
    ]);

    const latestUpdatedAt =
      getLatestUpdatedAt(product) ||
      product.updated_at ||
      new Date().toISOString();

    if (product.deleted_at && product.deleted_at !== '') {
      const fractionIds = (product.store_product_fractions ?? []).map(
        (f) => f.id,
      );
      await this.storeProducts.delete([product.store_id, product.id]);
      await this.removeBarcodeIndexForProduct(product.store_id, [
        product.id,
        ...fractionIds,
      ]);
      return;
    }

    if (
      !existing ||
      shouldReplaceExisting(latestUpdatedAt, existing.updated_at, forceUpdate)
    ) {
      const dbProduct: DBStoreProduct = {
        ...product,
        _lastSynced: new Date(),
        updated_at: latestUpdatedAt,
      };

      await this.storeProducts.put(dbProduct);
      await this.updateBarcodeIndexForProduct(product);
    }
  }

  getSyncStatus(storeId: string) {
    return this.syncMetadata.get(storeId);
  }

  async updateSyncStatus(
    storeId: string,
    status: SyncResponseStatus,
    errorMessage?: string,
    totalProducts?: number,
  ) {
    const existing = await this.syncMetadata.get(storeId);

    await this.syncMetadata.put({
      id: storeId,
      storeId,
      lastSync:
        status === SyncResponseStatus.IDLE
          ? new Date()
          : existing?.lastSync || new Date(0),
      totalProducts: totalProducts ?? existing?.totalProducts ?? 0,
      syncStatus: status,
      errorMessage,
    });
  }

  async clearStoreData(storeId: string): Promise<void> {
    await this.transaction(
      'rw',
      this.storeProducts,
      this.syncMetadata,
      this.productBarcodes,
      async () => {
        await this.storeProducts.where('store_id').equals(storeId).delete();
        await this.syncMetadata.where('storeId').equals(storeId).delete();
        await this.productBarcodes.where('store_id').equals(storeId).delete();
      },
    );
  }

  // ─── Barcode index helpers ────────────────────────────────────────

  /**
   * Extract all barcode index entries from a product and its fractions.
   */
  extractBarcodeEntries(product: StoreProduct): BarcodeIndexEntry[] {
    const entries: BarcodeIndexEntry[] = [];
    const storeId = product.store_id;

    for (const bc of (product.barcodes as Barcode[]) ?? []) {
      entries.push({
        store_id: storeId,
        barcode_value: String(bc.value),
        product_id: product.id,
        product_name: product.name ?? '',
      });
    }

    for (const frac of product.store_product_fractions ?? []) {
      for (const bc of (frac.barcodes as Barcode[]) ?? []) {
        entries.push({
          store_id: storeId,
          barcode_value: String(bc.value),
          product_id: frac.id,
          product_name: frac.name ?? product.name ?? '',
        });
      }
    }

    return entries;
  }

  /**
   * Rebuild the entire barcode index for a store from scratch.
   */
  async rebuildBarcodeIndex(storeId: string): Promise<void> {
    const products = await this.getStoreProducts(storeId);
    await this.transaction('rw', this.productBarcodes, async () => {
      await this.productBarcodes.where('store_id').equals(storeId).delete();
      const entries: BarcodeIndexEntry[] = [];
      for (const p of products) {
        entries.push(...this.extractBarcodeEntries(p));
      }
      if (entries.length > 0) {
        await this.productBarcodes.bulkPut(entries);
      }
    });
  }

  /**
   * Update the barcode index for a single product (parent + fractions).
   * Removes old entries then inserts current ones.
   */
  async updateBarcodeIndexForProduct(product: StoreProduct): Promise<void> {
    const productIds = [
      product.id,
      ...(product.store_product_fractions ?? []).map((f) => f.id),
    ];
    await this.transaction('rw', this.productBarcodes, async () => {
      for (const pid of productIds) {
        await this.productBarcodes
          .where('[store_id+product_id]')
          .equals([product.store_id, pid])
          .delete();
      }
      const entries = this.extractBarcodeEntries(product);
      if (entries.length > 0) {
        await this.productBarcodes.bulkPut(entries);
      }
    });
  }

  /**
   * Remove all barcode index entries for a product and its fractions.
   */
  async removeBarcodeIndexForProduct(
    storeId: string,
    productIds: string[],
  ): Promise<void> {
    await this.transaction('rw', this.productBarcodes, async () => {
      for (const pid of productIds) {
        await this.productBarcodes
          .where('[store_id+product_id]')
          .equals([storeId, pid])
          .delete();
      }
    });
  }

  /**
   * Find all products that own a specific barcode in a store.
   */
  findBarcodeOwner(
    storeId: string,
    barcodeValue: string,
  ): Promise<BarcodeIndexEntry[]> {
    // Try exact match first, then try with/without leading zero for legacy data
    return this.productBarcodes
      .where('[store_id+barcode_value]')
      .anyOf([
        [storeId, barcodeValue],
        ...(barcodeValue.startsWith('0')
          ? [[storeId, barcodeValue.substring(1)]]
          : []),
        [storeId, '0' + barcodeValue],
      ])
      .toArray();
  }

  /**
   * Check which of the given barcode values conflict with products
   * outside the given product-ID set.
   */
  async getConflictsForProduct(
    storeId: string,
    excludeProductIds: string[],
    barcodeValues: string[],
  ): Promise<BarcodeIndexEntry[]> {
    const conflicts: BarcodeIndexEntry[] = [];
    for (const val of barcodeValues) {
      const owners = await this.findBarcodeOwner(storeId, val);
      for (const owner of owners) {
        if (!excludeProductIds.includes(owner.product_id)) {
          conflicts.push(owner);
        }
      }
    }
    return conflicts;
  }
}

export const productDb = new ProductDatabase();
